import { useCallback, useEffect, useState } from "react";
import { powerbillService } from "../services/powerBill.services";
import { useAuth } from "@/app/providers/useAuth";
import { PowerbillResidentialPlan } from "../modal/powerBill.types.ts";
import { runAsync } from "@/shared/loading/runAsync";
import { propertyService } from "@/features/properties/model/propertyServices";
import { isValidUUID } from "@/features/properties/model/guestSession";
import { toastService } from "@/shared/toast/toast.service";
import { routes } from "@/app/navigation/routes.ts";
import { useNavigate } from "react-router-dom";

export type UtilityOption = {
  label: string;
  value: string;
};

export const usePowerbill = (selectedProperty: string) => {
  const { user } = useAuth();
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [street, setStreet] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [city, setCity] = useState("");
  const [stateName, setStateName] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [utilityName, setUtilityName] = useState<string>("");
  const [suppliers, setSuppliers] = useState<string[]>([]);
  const [zone, setZone] = useState("North");
  const [pastYearUsage, setPastYearUsage] = useState("2000");
  const [currentYearUsage, setCurrentYearUsage] = useState("0");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [plans, setPlans] = useState<PowerbillResidentialPlan[]>([]);
  const [selectedLengths, setSelectedLengths] = useState<number[]>([]);
  const [term, setTerm] = useState<number[]>([]);

  const getEletricityEligible = useCallback(
    async (zip: string, state: string, stateName: string) => {
      const response = await runAsync(() =>
        powerbillService.getEletricityEligible(zip, stateName, state)
      );
      return response;
    },
    []
  );

  const navigate = useNavigate();

  const getEletricityPlans = useCallback(
    async (zip: string, utilityName: string, term: number[], propertyType: string) => {
      const response = await runAsync(() =>
        powerbillService.getEletricityPlans(zip, utilityName, term, propertyType)
      );
      return response;
    },
    []
  );

  useEffect(() => {
    if (selectedProperty && isValidUUID(selectedProperty)) {
      const fetchPropertyDetails = async () => {
        const propertyDetails = await propertyService.getPropertyById(selectedProperty);
        return propertyDetails;
      };
      fetchPropertyDetails().then((propertyDetails) => {
        if (propertyDetails) {
          setZipCode(propertyDetails.address.zip);
          setStateName(propertyDetails.address.state);
          getEletricityPlans(
            propertyDetails.address.zip,
            utilityName || "",
            term,
            propertyDetails.property_type || "residential"
          ).then((response) => {
            if (response?.data) {
              console.log("response", response.data);
              setSuppliers(response?.data?.unqiue_suppilers || []);
              setPlans(
                response?.data?.utilities?.flatMap((utility) => utility.residentialPlans) || []
              );
            }
          });
        }
      });
    }
  }, [selectedProperty, utilityName, term, getEletricityPlans]);

  const enrollInPlanCallback = useCallback(
    async (planId: string): Promise<boolean> => {
      const response = await runAsync(() =>
        powerbillService.enrollInPlan({
          user_id: user?.id || 0,
          property_id: selectedProperty || "",
          plan_id: planId,
          zip_code: zipCode || "",
          state: stateName || "",
        })
      );

      if (response?.data?.headless_enroll_url) {
        toastService.show("Plan enrolled successfully", "success");
        navigate(routes.energyBotScreen, {
          state: {
            headlessEnrollmentUrl: response?.data?.headless_enroll_url,
          },
        });
        return true;
      } else {
        toastService.show(response?.message || "Failed to enroll plan", "error");
        return false;
      }
    },
    [selectedProperty, user?.id, zipCode, stateName, navigate]
  );

  return {
    getEletricityEligible,
    getEletricityPlans,
    enrollInPlanCallback,
    selectedProperty,
    zipCode,
    setZipCode,
    city,
    setCity,
    stateName,
    setStateName,
    firstName,
    setFirstName,
    middleName,
    setMiddleName,
    lastName,
    setLastName,
    street,
    setStreet,
    user,
    isEditing,
    setIsEditing,
    utilityName,
    setUtilityName,
    zone,
    setZone,
    pastYearUsage,
    setPastYearUsage,
    currentYearUsage,
    setCurrentYearUsage,
    isFilterOpen,
    setIsFilterOpen,
    plans,
    setPlans,
    selectedLengths,
    setSelectedLengths,
    suppliers,
    term,
    setTerm,
  };
};
